import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import { LoaderComponent } from '@sukna/shared';
import { AppInitializationService } from './core/services/app-initialization.service';
import { AppService } from './core/services/app.service';
import { VersionCheckService } from './core/services/version-check.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    imports: [RouterOutlet, AsyncPipe, LoaderComponent],
})
export class AppComponent implements OnInit {
    readonly initializing$: Observable<boolean>;
    readonly translationsReady$: Observable<boolean>;
    private destroyRef = inject(DestroyRef);
    private readonly versionCheckService = inject(VersionCheckService);

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private titleService: Title,
        private appInitService: AppInitializationService,
        private appService: AppService,
    ) {
        this.initializing$ = combineLatest([
            this.appInitService.initializing$,
            this.appService.translationsLoading$,
        ]).pipe(map(([appInitializing, translationsLoading]) => appInitializing || translationsLoading));
        this.translationsReady$ = this.appService.translationsLoading$.pipe(
            map((loading) => !loading),
        );
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map((route) => {
                    while (route.firstChild) route = route.firstChild;
                    return route;
                }),
                filter((route) => route.outlet === 'primary'),
                switchMap((route) => route.data.pipe(
                    map((routeData: any) => routeData['title'] ?? ''),
                )),
                map((title) => (title ? title + ' | ' : '') + 'LMS - Sukna capital'),
                distinctUntilChanged(),
                tap((title) => this.titleService.setTitle(title)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    ngOnInit(): void {
        // Deploy soft-updates (enabled only in deployed dev/qa/staging/prod environments).
        this.versionCheckService.start();
    }
}
